import { useState } from 'react';
import { Bell } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useNavigate } from 'react-router-dom';
import { usePushInbox } from '../contexts/PushNotificationContext';

/**
 * Header bell — shows the unread push count and opens a compact inbox popover
 * with the last 10 notifications. Clicking an item marks all as read and
 * navigates to its deep link.
 */
export default function PushInboxBell({ compact = false }) {
  const { items, unread, markAllRead, clear } = usePushInbox();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleOpen = (o) => {
    setOpen(o);
    if (o && unread > 0) markAllRead();
  };

  const handleClick = (item) => {
    setOpen(false);
    if (item.url) {
      try {
        navigate(item.url);
      } catch (_) {
        window.location.href = item.url;
      }
    }
  };

  const size = compact ? 20 : 22;
  const badge = unread > 9 ? '9+' : String(unread);

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-md hover:bg-[#F1F5F9] transition-colors"
          aria-label="Notifications"
          data-testid="push-inbox-bell"
        >
          <Bell size={size} weight={unread > 0 ? 'fill' : 'regular'} className={unread > 0 ? 'text-indigo-600' : 'text-[#475569]'} />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="push-unread-badge"
            >
              {badge}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" data-testid="push-inbox-popover">
        <div className="p-3 border-b border-[#E2E8F0] flex items-center justify-between">
          <p className="font-outfit font-semibold text-sm text-[#0F172A]">Notifications reçues</p>
          {items.length > 0 && (
            <button
              onClick={() => {
                clear();
                setOpen(false);
              }}
              className="text-xs text-red-600 hover:underline"
              data-testid="push-inbox-clear"
            >
              Effacer
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#94A3B8]">
              <Bell size={24} className="mx-auto mb-2 opacity-50" />
              <p>Aucune notification reçue.</p>
              <p className="text-xs mt-1">Elles apparaîtront ici automatiquement.</p>
            </div>
          ) : (
            items.slice(0, 10).map((item) => (
              <button
                key={item.id}
                onClick={() => handleClick(item)}
                className="w-full text-left p-3 border-b border-[#F1F5F9] hover:bg-indigo-50/50 transition-colors"
                data-testid={`push-inbox-item-${item.id}`}
              >
                <p className="font-medium text-sm text-[#0F172A] line-clamp-1">{item.title}</p>
                <p className="text-xs text-[#475569] line-clamp-2 mt-0.5">{item.body}</p>
                <p className="text-[10px] text-[#94A3B8] mt-1">
                  {new Date(item.at).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                </p>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
